import {
  isContentRating,
  hubItemKindFromLegacyContentKind,
  isHubItemKind,
  legacyContentKindFromHubItemKind,
  normalizeHubItemDto,
  normalizeHubItemCreator,
  normalizeHubItemStats,
  anonymousHubSession,
  memberHubSession,
  normalizeHubMember,
  normalizeHubPublicProfile,
  type AdminUserInfo,
  type HubClient,
  type ContentRating,
  type HubItemDto,
  type HubComment,
  type HubListing,
  type HubListingDetail,
  type HubPage,
  type HubProfile,
  type HubPublicProfileDto,
  type HubPublishInput,
  type HubQuery,
  type HubSessionDto,
} from "@roqeet/core";

const API_UNSUPPORTED_MESSAGE =
  "RoQeet Hub API read-only adapter does not support this operation yet.";

interface ApiHubItemListResponse {
  items?: HubItemDto[];
  nextCursor?: string | null;
}

interface ApiHubItemResponse {
  item?: HubItemDto;
  project?: HubItemDto;
}

interface ApiHubSessionResponse extends HubSessionDto {}

interface ApiHubProfileResponse {
  profile?: HubPublicProfileDto;
}

function unsupported(): Promise<never> {
  return Promise.reject(new Error(API_UNSUPPORTED_MESSAGE));
}

function toMillis(value: string | number | null | undefined): number {
  if (typeof value === "number") return value;
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function rating(value: string | null | undefined): ContentRating {
  return value && isContentRating(value) ? value : "unrated";
}

function mapHubItemKind(item: HubItemDto): HubListing["kind"] {
  if (!isHubItemKind(item.kind)) {
    throw new Error(`Unsupported Hub item kind from API: ${item.kind}`);
  }
  return legacyContentKindFromHubItemKind(item.kind) ?? "game-card";
}

function mapHubItem(item: HubItemDto): HubListing {
  const creator = normalizeHubItemCreator(item.ownerId, item.creator);
  const stats = normalizeHubItemStats(item.stats);
  return {
    id: item.id,
    kind: mapHubItemKind(item),
    itemKind: item.kind,
    ...(item.botSubtype !== undefined ? { botSubtype: item.botSubtype } : {}),
    ...(item.parentItemId !== undefined ? { parentItemId: item.parentItemId } : {}),
    itemStatus: item.status,
    ...(item.visibility ? { itemVisibility: item.visibility } : {}),
    title: item.title,
    ...(item.summary ? { summary: item.summary } : {}),
    ...(item.description ? { description: item.description } : {}),
    author: {
      id: creator.id,
      handle: creator.handle ?? creator.displayName,
      ...(creator.avatarUrl ? { avatarUrl: creator.avatarUrl } : {}),
    },
    tags: item.tags ?? [],
    rating: rating(item.rating),
    downloadCount: stats.downloads,
    ratingAvg: stats.ratingAverage,
    ratingCount: stats.ratingCount,
    commentCount: stats.comments,
    viewCount: stats.views,
    bookmarkCount: stats.bookmarks,
    sourceVisibility: "public",
    statsPrivacy: "uncollected",
    createdAt: toMillis(item.createdAt),
    updatedAt: toMillis(item.updatedAt ?? item.publishedAt),
  };
}

/**
 * Provider-neutral HTTP HubClient adapter. It is opt-in only and currently
 * read-only until auth, package payload delivery, and write APIs exist.
 */
export class ApiHubClient implements HubClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  isConfigured(): boolean {
    return this.baseUrl.length > 0;
  }

  supportsLegacyDownloads(): boolean {
    return false;
  }

  currentProfile(): HubProfile | null {
    return null;
  }

  getGoogleLoginUrl(redirectTo: string = globalThis.location?.href ?? "/"): string {
    this.ensureConfigured();
    const url = new URL(`${this.baseUrl}/api/auth/google/start`);
    url.searchParams.set("redirectTo", redirectTo);
    return url.toString();
  }

  async getSession(): Promise<HubSessionDto> {
    this.ensureConfigured();
    const url = new URL(`${this.baseUrl}/api/hub/session`);
    const session = await this.request<ApiHubSessionResponse>(url);
    if (session.state === "member" && session.member) {
      return memberHubSession(normalizeHubMember(session.member.id, session.member));
    }
    return anonymousHubSession();
  }

  signIn(_idToken: string): Promise<HubProfile> {
    const location = globalThis.location;
    if (location) {
      location.assign(this.getGoogleLoginUrl(location.href));
      return new Promise(() => {});
    }
    return unsupported();
  }

  async signOut(): Promise<void> {
    this.ensureConfigured();
    await this.request<void>(new URL(`${this.baseUrl}/api/auth/logout`), { method: "POST" });
  }

  subscribe(onChange: (profile: HubProfile | null) => void): () => void {
    onChange(null);
    return () => {};
  }

  async listHubItems(query: HubQuery): Promise<HubPage<HubItemDto>> {
    this.ensureConfigured();
    const url = this.hubItemsUrl(query);

    const page = await this.request<ApiHubItemListResponse>(url);
    return {
      items: (page.items ?? []).map(normalizeHubItemDto),
      ...(page.nextCursor ? { nextCursor: page.nextCursor } : {}),
    };
  }

  async getHubItem(idOrSlug: string): Promise<HubItemDto | null> {
    this.ensureConfigured();
    const url = new URL(`${this.baseUrl}/api/hub/items/${encodeURIComponent(idOrSlug)}`);
    const response = await this.requestNullable<ApiHubItemResponse>(url);
    const item = response?.item ?? response?.project ?? null;
    return item ? normalizeHubItemDto(item) : null;
  }

  async list(query: HubQuery): Promise<HubPage<HubListing>> {
    const page = await this.listHubItems(query);
    return {
      items: page.items.map(mapHubItem),
      ...(page.nextCursor ? { nextCursor: page.nextCursor } : {}),
    };
  }

  download(_listingId: string): Promise<HubListingDetail> {
    return unsupported();
  }

  publish(_input: HubPublishInput): Promise<HubListing> {
    return unsupported();
  }

  unpublish(_listingId: string): Promise<void> {
    return unsupported();
  }

  getProfile(_profileId: string): Promise<HubProfile | undefined> {
    return Promise.resolve(undefined);
  }

  async getPublicProfile(idOrHandle: string): Promise<HubPublicProfileDto | null> {
    this.ensureConfigured();
    const url = new URL(`${this.baseUrl}/api/hub/profiles/${encodeURIComponent(idOrHandle)}`);
    const response = await this.requestNullable<ApiHubProfileResponse>(url);
    return response?.profile ? normalizeHubPublicProfile(response.profile) : null;
  }

  updateProfile(_patch: Partial<Pick<HubProfile, "handle" | "bio">>): Promise<HubProfile> {
    return unsupported();
  }

  rate(_listingId: string, _stars: number): Promise<void> {
    return unsupported();
  }

  listComments(_listingId: string, _cursor?: string): Promise<HubPage<HubComment>> {
    return Promise.resolve({ items: [] });
  }

  addComment(_listingId: string, _body: string): Promise<HubComment> {
    return unsupported();
  }

  deleteComment(_commentId: string): Promise<void> {
    return unsupported();
  }

  follow(_profileId: string): Promise<void> {
    return unsupported();
  }

  unfollow(_profileId: string): Promise<void> {
    return unsupported();
  }

  updateListing(_listingId: string, _patch: Partial<HubPublishInput>): Promise<HubListing> {
    return unsupported();
  }

  bookmark(_listingId: string): Promise<void> {
    return unsupported();
  }

  unbookmark(_listingId: string): Promise<void> {
    return unsupported();
  }

  incrementView(_listingId: string): Promise<void> {
    return Promise.resolve();
  }

  getProfileByHandle(_handle: string): Promise<HubProfile | undefined> {
    return Promise.resolve(undefined);
  }

  checkAdmin(): Promise<boolean> {
    return Promise.resolve(false);
  }

  adminListUsers(_limit: number, _offset: number): Promise<AdminUserInfo[]> {
    return unsupported();
  }

  adminRemoveListing(_listingId: string): Promise<void> {
    return unsupported();
  }

  private ensureConfigured(): void {
    if (!this.isConfigured()) {
      throw new Error("RoQeet Hub API URL is not configured.");
    }
  }

  private hubItemsUrl(query: HubQuery): URL {
    const url = new URL(`${this.baseUrl}/api/hub/items`);
    if (query.cursor) url.searchParams.set("cursor", query.cursor);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.itemKind) url.searchParams.set("kind", query.itemKind);
    if (query.botSubtype) url.searchParams.set("botSubtype", query.botSubtype);
    if (!query.itemKind && query.kind) {
      url.searchParams.set("kind", hubItemKindFromLegacyContentKind(query.kind));
    }
    return url;
  }

  private async request<T>(url: URL, init?: RequestInit): Promise<T> {
    let response: Response;
    try {
      response = await fetch(url, { credentials: "include", ...init });
    } catch (error) {
      throw new Error(`RoQeet Hub API request failed: ${(error as Error).message}`);
    }

    const text = await response.text();
    if (!response.ok) {
      throw new Error(`RoQeet Hub API request failed (${response.status}): ${text}`);
    }

    try {
      return (text ? JSON.parse(text) : {}) as T;
    } catch {
      throw new Error("RoQeet Hub API returned invalid JSON.");
    }
  }

  private async requestNullable<T>(url: URL): Promise<T | null> {
    let response: Response;
    try {
      response = await fetch(url, { credentials: "include" });
    } catch (error) {
      throw new Error(`RoQeet Hub API request failed: ${(error as Error).message}`);
    }

    const text = await response.text();
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`RoQeet Hub API request failed (${response.status}): ${text}`);
    }

    try {
      return (text ? JSON.parse(text) : {}) as T;
    } catch {
      throw new Error("RoQeet Hub API returned invalid JSON.");
    }
  }
}
